import React, { Fragment } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PauseIcon from '@mui/icons-material/Pause';
import {
  Box,
  Divider,
  FormLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

/**
 * Componente para renderizar una pregunta de tipo texto con validación y grabación de voz opcional
 *
 * @param {Object} props
 * @param {number} props.questionId - ID único de la pregunta
 * @param {string} props.typeQuestion - Tipo de la pregunta
 * @param {string} props.questionName - Texto de la pregunta
 * @param {string} [props.description] - Descripción adicional de la pregunta
 * @param {number} props.index - Índice de la pregunta en el array
 * @param {Array<number>} props.unansweredQuestions - Array de índices de preguntas sin responder
 * @param {Object} props.questionRef - Ref para scroll automático
 * @param {Object} props.formValue - Valor actual del formulario
 * @param {string} props.formValue.value - Texto ingresado
 * @param {Function} props.onChange - Callback cuando cambia el texto
 * @param {Object} [props.settings] - Configuración del campo de texto
 * @param {string} [props.settings.textType='text'] - Tipo: 'text', 'number', 'email'
 * @param {boolean} [props.settings.haveLimit=false] - Si tiene límite de caracteres
 * @param {number} [props.settings.wordSizeLimit=0] - Límite máximo de caracteres
 * @param {boolean} [props.settings.microphoneEnabled=false] - Si permite grabación de voz
 * @param {Object} props.sliderErrors - Objeto con mensajes de error
 * @param {Function} props.onErrorClear - Callback para limpiar errores
 * @param {string} [props.primaryColor='#03aae4'] - Color primario del tema
 * @param {boolean} [props.browserSupportsSpeechRecognition=false] - Si el navegador soporta reconocimiento de voz
 * @param {number|null} props.dictatingIndex - Índice de la pregunta actualmente dictando
 * @param {boolean} props.isRecording - Si está grabando actualmente
 * @param {number} props.recordingTime - Tiempo actual de grabación en segundos
 * @param {Object} props.resetCount - Conteo de intentos de grabación por pregunta
 * @param {number} props.maxResets - Máximo de intentos permitidos
 * @param {Function} props.onStartDictation - Callback para iniciar dictado
 * @param {Function} props.onPauseDictation - Callback para pausar dictado
 * @param {Function} props.onResumeDictation - Callback para reanudar dictado
 * @param {Function} props.onStopDictation - Callback para detener dictado
 * @param {Function} props.canRecord - Función que verifica si puede grabar
 * @param {Function} props.getMaxRecordingTime - Función que obtiene tiempo máximo de grabación
 * @param {Function} props.getRecordingTooltip - Función que obtiene tooltip de grabación
 * @param {Function} props.formatTime - Función para formatear tiempo
 * @param {Function} props.getTimeColor - Función para obtener color según tiempo
 * @param {boolean} [props.showDivider=true] - Si se debe mostrar el divisor al final
 */
const TextQuestion = ({
  questionId,
  typeQuestion,
  questionName,
  description,
  index,
  unansweredQuestions = [],
  questionRef,
  formValue = {},
  onChange,
  settings = {},
  sliderErrors = {},
  onErrorClear,
  primaryColor = '#03aae4',
  browserSupportsSpeechRecognition = false,
  dictatingIndex = null,
  isRecording = false,
  recordingTime = {},
  resetCount = {},
  pausedQuestions = new Set(),
  maxResets = 3,
  onStartDictation,
  onPauseDictation,
  onResumeDictation,
  onStopDictation,
  canRecord,
  getMaxRecordingTime,
  getRecordingTooltip,
  formatTime,
  getTimeColor,
  showDivider = true,
  hasOwnBold,
}) => {
  const hasError = unansweredQuestions.includes(index) || !!sliderErrors[index];
  const textValue = formValue?.value || '';
  const currentRecordingTime = recordingTime[index] || 0;
  const validationType = settings?.textType || 'text';
  const hasLimit = settings?.haveLimit === true;
  const maxChars = settings?.wordSizeLimit || 0;
  const micEnabled = settings?.microphoneEnabled;

  // Determinar número de filas según el tipo
  const getRows = () => {
    if (validationType === 'number' || validationType === 'email') {
      return 1;
    }
    return 4;
  };

  // Manejar cambio de texto con validación
  const handleTextChange = (event) => {
    const inputValue = event.target.value;
    let filteredValue = inputValue;

    // Validar según el tipo
    if (validationType === 'number') {
      filteredValue = inputValue.replace(/[^0-9]/g, '');
    } else if (validationType === 'email') {
      filteredValue = inputValue.replace(/[^a-zA-Z0-9@.\-_]/g, '');
    }

    // Si tiene límite, no permitir más caracteres
    if (hasLimit && maxChars > 0 && filteredValue.length > maxChars) {
      return;
    }

    // Crear evento modificado con el valor filtrado
    const modifiedEvent = {
      ...event,
      target: {
        ...event.target,
        value: filteredValue,
      },
    };

    onChange(modifiedEvent, index);

    // Limpiar error si existe
    if (sliderErrors[index] && onErrorClear) {
      onErrorClear(index);
    }
  };

  // Renderizar contador de caracteres
  const renderCharCounter = () => {
    if (!hasLimit) return null;

    const currentLength = textValue.length;
    const isOverLimit = currentLength >= maxChars;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: isOverLimit ? 'error.main' : 'text.secondary',
            fontWeight: isOverLimit ? 'bold' : 'normal',
          }}
        >
          {currentLength}/{maxChars}
        </Typography>
      </Box>
    );
  };

  // Renderizar controles de micrófono
  const renderMicControls = () => {
    if (!micEnabled) return null;

    const isDictating = dictatingIndex === index;
    const isPaused = pausedQuestions.has(index);
    const attempts = resetCount[index] || 0;
    const remainingAttempts = maxResets - attempts;

    return (
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          bottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
        }}
      >
        {/* Contador de tiempo */}
        {(isDictating || isPaused) && (
          <Box
            sx={{
              bgcolor: 'background.paper',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              boxShadow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              color: getTimeColor(
                currentRecordingTime,
                getMaxRecordingTime(index)
              ),
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isRecording ? 'error.main' : 'grey.400',
                animation: isRecording
                  ? 'pulse 1s ease-in-out infinite'
                  : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                },
              }}
            />
            <span>
              {formatTime(currentRecordingTime)} /{' '}
              {formatTime(getMaxRecordingTime(index))}
            </span>
          </Box>
        )}

        {/* Indicador de intentos restantes */}
        {!isDictating && !isPaused && attempts > 0 && canRecord(index) && (
          <Box
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.dark',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.65rem',
              fontWeight: 'bold',
            }}
          >
            {remainingAttempts}{' '}
            {remainingAttempts === 1 ? 'intento' : 'intentos'}
          </Box>
        )}

        {/* Botones de control */}
        {/* Botones de control */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {!isDictating && !isPaused ? (
            // Botón para iniciar (estado normal)
            <Tooltip title={getRecordingTooltip(index)} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onStartDictation(index)}
                  disabled={!canRecord(index)}
                  color="primary"
                  aria-label={getRecordingTooltip(index)}
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { boxShadow: 2 },
                    '&.Mui-disabled': {
                      bgcolor: 'grey.200',
                      color: 'grey.400',
                      pointerEvents: 'auto',
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  {!canRecord(index) ? <MicOffIcon /> : <MicIcon />}
                </IconButton>
              </span>
            </Tooltip>
          ) : isPaused ? ( // ✅ AGREGAR ESTE CASO
            // Botón para reanudar pregunta pausada
            <Tooltip title="Reanudar dictado" arrow>
              <IconButton
                size="small"
                onClick={() => onStartDictation(index)}
                color="success"
                aria-label="Reanudar dictado"
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <MicIcon />
              </IconButton>
            </Tooltip>
          ) : (
            // Botones cuando está activamente dictando esta pregunta
            <>
              {/* Botón pausar/reanudar */}
              <IconButton
                size="small"
                onClick={isRecording ? onPauseDictation : onResumeDictation}
                color={isRecording ? 'warning' : 'success'}
                aria-label={isRecording ? 'Pausar dictado' : 'Reanudar dictado'}
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { boxShadow: 2 },
                  ...(isRecording && {
                    animation: 'recording 1.2s ease-in-out infinite',
                    '@keyframes recording': {
                      '0%, 100%': {
                        transform: 'scale(1)',
                        boxShadow: '0 0 0 0 rgba(237, 108, 2, 0.7)',
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 0 0 8px rgba(237, 108, 2, 0)',
                      },
                    },
                  }),
                }}
              >
                {isRecording ? <PauseIcon /> : <MicIcon />}
              </IconButton>

              {/* Botón detener */}
              <IconButton
                size="small"
                onClick={() => onStopDictation(false)}
                color="error"
                aria-label="Detener dictado"
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <FormLabel
        style={{
          fontSize: '1.1',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '1.1em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      <Box sx={{ position: 'relative', margin: '1.2em 0' }}>
        <TextField
          inputRef={questionRef}
          id={`${questionId}-${typeQuestion}`}
          name={`${questionId}-${typeQuestion}`}
          value={textValue}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
          multiline
          rows={getRows()}
          variant="outlined"
          fullWidth
          onChange={handleTextChange}
          error={hasError}
          helperText={
            sliderErrors[index] ||
            (unansweredQuestions.includes(index) && 'Este campo es requerido')
          }
          type={validationType === 'email' ? 'email' : 'text'}
          inputProps={{
            inputMode: validationType === 'number' ? 'numeric' : 'text',
            pattern: validationType === 'number' ? '[0-9]*' : undefined,
          }}
          sx={{
            pr: micEnabled ? 7 : 0,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
              },
            },
            '& label.Mui-focused': {
              color: primaryColor,
            },
          }}
        />

        {renderMicControls()}
      </Box>

      {renderCharCounter()}

      {showDivider && <Divider variant="middle" sx={{ marginTop: '10px' }} />}
    </Box>
  );
};

export default TextQuestion;
